import { buildAudioSnapshot, snapshotKey } from './audio-devices';
import type { MicPermission } from './mic-health';
import { rumAction, logAudio } from '../lib/rum';

// =====================================================================
// Reporting the SDR's microphone setup.
//
// SDRs default to a laptop mic without noticing and prospects can't understand
// them. We report the setup so it is queryable (which SDRs are on a built-in mic?
// did a headset drop mid-shift?) and visible while watching a session replay.
//
// TWO SURFACES, on purpose:
//   - a RUM action  -> a marker on the Session Replay timeline + queryable facets.
//                      Kept compact; these become RUM facets.
//   - a browser log -> the FULL device list. Logs take a bigger payload and retain
//                      longer, and that list is the corpus the built-in-vs-external
//                      heuristic will be derived from (see audio-devices.ts).
// Both no-op outside prod/authed — see lib/rum.ts.
//
// QUERYING THIS: group by `resolved_label`, NEVER by `resolved_id`.
// MediaDeviceInfo.deviceId is SALTED per browser profile/session and rotates (e.g.
// when site data is cleared) — verified 2026-07-15, where one physical mic reported
// two different ids across two browser contexts on the same host. So `resolved_id`
// is meaningful only WITHIN a session (which is all the dedupe below needs, since
// its ref resets on teardown). The label is the only cross-session device identity.
// =====================================================================

export type AudioReportReason = 'acquire' | 'device_change' | 'user_pick';

// A mutable holder for the last reported key. Matches a React ref structurally so
// callers pass their ref straight in, without this module importing React.
export interface KeyRef {
  current: string | null;
}

// Report the current mic setup, but ONLY on a real transition.
//
// Deduped on snapshotKey, which keys off the RESOLVED device — so an unplug under a
// constant 'default' still reports, while a hotplug burst (one physical plug often
// fires several deviceChange events, and permission-grant fires one of its own as
// labels populate) collapses to a single event.
export function reportAudioSnapshot(
  reason: AudioReportReason,
  inputs: readonly { deviceId: string; label: string; groupId: string }[],
  selectedId: string | null,
  lastKey: KeyRef,
): void {
  const snapshot = buildAudioSnapshot(inputs, selectedId);
  const key = snapshotKey(snapshot);
  if (key === lastKey.current) return;
  lastKey.current = key;
  rumAction('cockpit.audio.devices', {
    reason,
    selected_id: snapshot.selectedId,
    selected_pseudo: snapshot.selectedPseudo,
    resolved_id: snapshot.resolvedId,
    resolved_label: snapshot.resolvedLabel,
    real_input_count: snapshot.realInputCount,
    idle_external_label: snapshot.idleExternalLabel,
  });
  // `event` is the STABLE key dashboards/monitors filter on — never the message
  // title, which is prose and will drift (repo convention, see infrastructure/
  // datadog/monitors/features.ts).
  logAudio('devices', { event: 'cockpit_audio_devices', reason, ...snapshot });
}

// The mic never came up: permission denied, no hardware, or the device id we asked
// for did not exist. An SDR in this state is wholly broken (the picker renders empty
// and disabled), so this is an ERROR — today it surfaces only as a tooltip string.
// NOT deduped: every failure is worth seeing.
export function reportAudioFailure(stage: string, err: unknown): void {
  // NOT named `error`: that is a RESERVED Datadog log attribute expecting an OBJECT
  // (error.message / error.kind / error.stack). A flat string there gets picked up by
  // the Error Tracking pipeline and mangled, so the monitor below could never key on
  // it reliably.
  const error_message = err instanceof Error ? err.message : String(err);
  rumAction('cockpit.audio.failed', { stage, error_message });
  logAudio('failed', { event: 'cockpit_audio_failed', stage, error_message }, 'error');
}

// The mic has been gone long enough to stop expecting it back (cockpit/mic-health.ts).
// This, not reportAudioFailure above, is the alertable signal: a lone rejection is
// mostly transient, whereas reaching this state means the SDR cannot work.
//
// ONCE PER EPISODE — a blocked SDR stays blocked for hours, so the caller owns the
// transition and this must never be reached from a render or a tick.
export function reportMicBlocked(fields: {
  permission: MicPermission;
  error_message: string | null;
  in_pool: boolean;
  retried: boolean;
}): void {
  rumAction('cockpit.mic.blocked', fields);
  logAudio('mic blocked', { event: 'cockpit_mic_blocked', ...fields }, 'error');
}

// The counterpart; blocked_ms is how we tell whether the banner did anything.
export function reportMicRecovered(fields: { blocked_ms: number; in_pool: boolean }): void {
  rumAction('cockpit.mic.recovered', fields);
  logAudio('mic recovered', { event: 'cockpit_mic_recovered', ...fields });
}
